import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { activePanelView } from '@/lib/side-panel/panel-tabs';
import { selectWorktreeSourceRoot } from './conversation-worktree';
import { type DesktopShellStore, shownConversationState } from './store';

/** Subscribe the shell only to state it renders; actions retain stable identities. */
export function useDesktopShellView(store: DesktopShellStore) {
  return useStore(
    store,
    useShallow((state) => ({
      appSettings: state.appSettings,
      setAddRepositoryOpen: state.setAddRepositoryOpen,
      native: state.native,
      agents: state.agents,
      agentId: state.agentId,
      workspace: state.workspace,
      setPreferredOpenerId: state.setPreferredOpenerId,
      discoveringAgents: state.discoveringAgents,
      cloudRuntime: state.cloudRuntime,
      setCloudRuntime: state.setCloudRuntime,
      cloudProvisioningPhase: state.cloudProvisioningPhase,
      cloudProvisionError: state.cloudProvisionError,
      cloudProjectMissing: state.cloudProjectMissing,
      setCloudProvisionError: state.setCloudProvisionError,
      pendingCloudMessage:
        shownConversationState(state).pendingSend?.kind === 'cloud'
          ? (shownConversationState(state).pendingSend?.message ?? null)
          : null,
      cloudHandoffContext: shownConversationState(state).cloudHandoffContext,
      cloudRepositoryBusy: shownConversationState(state).cloudRepositoryBusy,
      conversationPlacementPending: shownConversationState(state).placementPending,
      localHandoffAgentId: state.localHandoffAgentId,
      localHandoffWorking: state.localHandoffWorking,
      chatId: state.chatId,
      initialTranscript: state.initialTranscript,
      restoredCollaboration: state.restoredCollaboration,
      restoredModelId: state.restoredModelId,
      messageRevision: state.messageRevision,
      conversationCache: state.conversationCache,
      syncedThreads: state.syncedThreads,
      setSyncedThreads: state.setSyncedThreads,
      setSyncedAgentConfigs: state.setSyncedAgentConfigs,
      setSyncedPullRequests: state.setSyncedPullRequests,
      syncedWorkspaces: state.syncedWorkspaces,
      setSyncedWorkspaces: state.setSyncedWorkspaces,
      setWorkspaceSyncError: state.setWorkspaceSyncError,
      setLoadingThreadIds: state.setLoadingThreadIds,
      generatedConversationTitle: state.generatedConversationTitle,
      setGeneratedConversationTitle: state.setGeneratedConversationTitle,
      conversationLayout: state.conversationLayout,
      previewModel: state.previewModel,
      setPreviewModel: state.setPreviewModel,
      previewEffort: state.previewEffort,
      setPreviewEffort: state.setPreviewEffort,
      sidePanelOpen: state.sidePanelOpen,
      setSidePanelOpen: state.setSidePanelOpen,
      sidePanelView: activePanelView(state.panelTabs),
      setPanelTabs: state.setPanelTabs,
      selectedSubagentId: state.selectedSubagentId,
      setSelectedSubagentId: state.setSelectedSubagentId,
      queuedFollowUps: state.queuedFollowUps,
      steeredFollowUps: state.steeredFollowUps,
      pausedFollowUpQueues: state.pausedFollowUpQueues,
      editingMessageId: state.editingMessageId,
      editingMessageContainer: state.editingMessageContainer,
      gitStatus: state.gitStatus,
      setLocalBranches: state.setLocalBranches,
      cloudPullRequest: state.cloudPullRequest,
      executionMode: state.executionMode,
      worktreeSourceRoot: selectWorktreeSourceRoot(state),
      workspaceEnvironment: state.workspaceEnvironment,
      setWorkspaceEnvironment: state.setWorkspaceEnvironment,
      panelMentionTargets: state.panelMentionTargets,
      personalWorkspaceRoot: state.personalWorkspaceRoot,
    })),
  );
}
